import React, {
  useEffect,
  useMemo,
  useState,
  useCallback,
} from "react";

import {
  Card,
  Row,
  Col,
  Table,
  Typography,
  Input,
  Select,
  Spin,
  Alert,
  Avatar,
  Tag,
  Modal,
  Button,
  Space,
  Statistic,
  Empty,
  Divider,
  Progress,
  Tooltip,
} from "antd";

import {
  SearchOutlined,
  TrophyOutlined,
  DownloadOutlined,
  TeamOutlined,
  FilePdfOutlined,
  CrownOutlined,
  BarChartOutlined,
  ReloadOutlined,
  SafetyCertificateOutlined,
  RiseOutlined,
  StarFilled,
  GoldOutlined,
} from "@ant-design/icons";

import {
  useAcademicYear,
} from "../../AcademicYearContext";

import api from "../../api/api";

import HighAveragesPagePDF from "./HighAveragesPagePDF";

import {
  PDFDownloadLink,
} from "@react-pdf/renderer";


const {
  Title,
  Text,
} = Typography;


/* ============================================================
   SCHOOL COLOR SYSTEM
   DEEP NAVY + GOLD + WHITE
============================================================ */

const COLORS = {
  navy: "#061A33",
  deepNavy: "#03101F",
  royalNavy: "#0A2A52",
  blue: "#0D3B73",
  brightBlue: "#145DA0",
  skyBlue: "#2F80C9",

  gold: "#D4A017",
  richGold: "#B8860B",
  brightGold: "#F4C542",
  softGold: "#FFF8DE",
  paleGold: "#FFFDF2",

  white: "#FFFFFF",
  offWhite: "#F8FAFC",
  paper: "#FFFFFF",

  text: "#102033",
  muted: "#66758A",

  border: "#D9E1EA",
  navyBorder: "#173B67",

  black: "#02070D",
};


/* ============================================================
   HELPERS
============================================================ */

const getInitials = (name = "") => {
  const parts = name
    .trim()
    .split(/\s+/)
    .filter(Boolean);

  if (!parts.length) return "?";

  if (parts.length === 1) {
    return parts[0]
      .charAt(0)
      .toUpperCase();
  }

  return (
    parts[0]
      .charAt(0)
      .toUpperCase() +
    parts[parts.length - 1]
      .charAt(0)
      .toUpperCase()
  );
};


const getAverage = (value) => {
  const number = Number(value);

  return Number.isFinite(number)
    ? number
    : 0;
};


const getRankStyle = (rank) => {
  const numericRank = Number(rank);

  if (numericRank === 1) {
    return {
      background:
        `linear-gradient(145deg, ${COLORS.brightGold}, ${COLORS.richGold})`,
      color: COLORS.deepNavy,
      border:
        `1px solid ${COLORS.brightGold}`,
      shadow:
        "0 6px 18px rgba(212,160,23,0.35)",
    };
  }

  if (numericRank === 2) {
    return {
      background:
        "linear-gradient(145deg,#F4F6F8,#C9D1DA)",
      color: COLORS.navy,
      border:
        "1px solid #B8C1CC",
      shadow:
        "0 5px 14px rgba(6,26,51,0.12)",
    };
  }

  if (numericRank === 3) {
    return {
      background:
        "linear-gradient(145deg,#D9B36C,#A97A25)",
      color: COLORS.white,
      border:
        "1px solid #A87825",
      shadow:
        "0 5px 14px rgba(168,120,37,0.22)",
    };
  }

  return {
    background:
      `linear-gradient(145deg, ${COLORS.navy}, ${COLORS.blue})`,
    color: COLORS.white,
    border:
      `1px solid ${COLORS.navyBorder}`,
    shadow:
      "0 4px 12px rgba(6,26,51,0.12)",
  };
};


const getAverageTone = (value) => {
  const average = getAverage(value);

  if (average >= 95) {
    return {
      color: COLORS.richGold,
      background: COLORS.softGold,
      border: "#E8CF75",
    };
  }

  if (average >= 90) {
    return {
      color: COLORS.navy,
      background: "#EEF4FB",
      border: "#B9CCE2",
    };
  }

  if (average >= 80) {
    return {
      color: "#31587F",
      background: "#F0F5FA",
      border: "#CBD9E7",
    };
  }

  return {
    color: "#6C4D13",
    background: "#FFF8E8",
    border: "#E8D5A4",
  };
};


const HighAveragesPage = () => {
  const {
    selectedYear,
  } = useAcademicYear();


  const [
    gradeClasses,
    setGradeClasses,
  ] = useState([]);

  const [
    selectedClass,
    setSelectedClass,
  ] = useState("");

  const [
    students,
    setStudents,
  ] = useState([]);

  const [
    loading,
    setLoading,
  ] = useState(false);

  const [
    loadingClasses,
    setLoadingClasses,
  ] = useState(false);

  const [
    error,
    setError,
  ] = useState("");

  const [
    search,
    setSearch,
  ] = useState("");

  const [
    showPDFModal,
    setShowPDFModal,
  ] = useState(false);

  const [
    reportData,
    setReportData,
  ] = useState(null);


  /* ============================================================
     LOAD GRADE CLASSES
  ============================================================ */

  const loadGradeClasses =
    useCallback(async () => {
      try {
        setLoadingClasses(true);

        const response =
          await api.get(
            "/api/users/grade-classes/"
          );

        setGradeClasses(
          response.data.results ||
          response.data ||
          []
        );
      } catch (error) {
        console.error(
          "Unable to load grade classes:",
          error
        );

        setError(
          "Unable to load grade classes."
        );
      } finally {
        setLoadingClasses(false);
      }
    }, []);


  useEffect(() => {
    loadGradeClasses();
  }, [
    loadGradeClasses,
  ]);


  /* ============================================================
     LOAD STUDENTS
  ============================================================ */

  const loadStudents =
    useCallback(async () => {
      if (!selectedClass) {
        setStudents([]);
        setReportData(null);
        return;
      }

      try {
        setLoading(true);
        setError("");

        const response =
          await api.get(
            "/top-student-summary/",
            {
              params: {
                academic_year:
                  selectedYear?.id,

                grade_class:
                  selectedClass,

                search,
              },
            }
          );

        const data =
          response.data?.results ||
          {};

        const studentList =
          Array.isArray(
            data.students
          )
            ? data.students
            : [];

        setStudents(studentList);

        setReportData({
          academic_year:
            data.academic_year,

          grade_class:
            data.grade_class,

          students:
            studentList,
        });

      } catch (error) {
        console.error(
          "Unable to load high performing students:",
          error
        );

        setStudents([]);
        setReportData(null);

        setError(
          "Unable to load top performing students."
        );
      } finally {
        setLoading(false);
      }
    }, [
      selectedClass,
      selectedYear,
      search,
    ]);


  useEffect(() => {
    if (selectedClass) {
      loadStudents();
    } else {
      setStudents([]);
      setReportData(null);
    }
  }, [
    selectedClass,
    loadStudents,
  ]);


  /* ============================================================
     DERIVED DATA
  ============================================================ */

  const selectedClassName =
    useMemo(() => {
      return (
        gradeClasses.find(
          (item) =>
            String(item.id) ===
            String(selectedClass)
        )?.name || ""
      );
    }, [
      gradeClasses,
      selectedClass,
    ]);


  const sortedStudents =
    useMemo(() => {
      return [...students].sort(
        (a, b) =>
          getAverage(
            b.yearly_average
          ) -
          getAverage(
            a.yearly_average
          )
      );
    }, [students]);


  const topStudent =
    sortedStudents[0] || null;

  const secondStudent =
    sortedStudents[1] || null;

  const thirdStudent =
    sortedStudents[2] || null;


  const classAverage =
    useMemo(() => {
      if (!students.length) {
        return "0.0";
      }

      const total =
        students.reduce(
          (sum, student) =>
            sum +
            getAverage(
              student.yearly_average
            ),
          0
        );

      return (
        total / students.length
      ).toFixed(1);
    }, [students]);


  /* ============================================================
     HANDLERS
  ============================================================ */

  const handleGradeClassChange =
    (value) => {
      setSelectedClass(value);
      setSearch("");
      setError("");
      setShowPDFModal(false);
    };


  const handleClearClass = () => {
    setSelectedClass("");
    setSearch("");
    setStudents([]);
    setReportData(null);
    setError("");
    setShowPDFModal(false);
  };


  const handleDownloadReport = () => {
    if (
      !students.length ||
      !reportData
    ) {
      return;
    }

    setShowPDFModal(true);
  };


  /* ============================================================
     TABLE
  ============================================================ */

  const columns = [
    {
      title: "Rank",
      dataIndex: "rank",
      key: "rank",
      width: 90,
      align: "center",

      render: (rank) => {
        const style =
          getRankStyle(rank);

        return (
          <Avatar
            size={42}
            style={{
              background:
                style.background,
              color:
                style.color,
              border:
                style.border,
              boxShadow:
                style.shadow,
              fontWeight: 900,
            }}
          >
            {rank}
          </Avatar>
        );
      },
    },

    {
      title: "Student",
      dataIndex: "student_name",
      key: "student_name",

      render: (
        name,
        record
      ) => (
        <Space size={12}>
          <Avatar
            size={43}
            style={{
              background:
                Number(
                  record.rank
                ) === 1
                  ? `linear-gradient(145deg, ${COLORS.brightGold}, ${COLORS.gold})`
                  : `linear-gradient(145deg, ${COLORS.navy}, ${COLORS.blue})`,

              color:
                Number(
                  record.rank
                ) === 1
                  ? COLORS.deepNavy
                  : COLORS.white,

              fontWeight: 900,

              boxShadow:
                "0 4px 12px rgba(6,26,51,0.14)",
            }}
          >
            {getInitials(name)}
          </Avatar>

          <div>
            <Text
              strong
              style={{
                color:
                  COLORS.text,
                fontSize: 14,
              }}
            >
              {name ||
                "Unnamed Student"}
            </Text>

            {Number(
              record.rank
            ) === 1 && (
              <div>
                <Tag
                  bordered={false}
                  icon={
                    <CrownOutlined />
                  }
                  style={{
                    marginTop: 3,
                    marginInlineEnd: 0,
                    background:
                      COLORS.softGold,
                    color:
                      COLORS.richGold,
                    fontSize: 10,
                    fontWeight: 800,
                    borderRadius: 20,
                  }}
                >
                  TOP PERFORMER
                </Tag>
              </div>
            )}
          </div>
        </Space>
      ),
    },

    {
      title: "Grade Class",
      dataIndex: "grade_class",
      key: "grade_class",

      render: (value) => (
        <Tag
          bordered={false}
          style={{
            background:
              "#EEF3F8",
            color:
              COLORS.navy,
            border:
              "1px solid #CCD8E5",
            borderRadius: 20,
            padding:
              "4px 12px",
            fontWeight: 700,
          }}
        >
          {value || "--"}
        </Tag>
      ),
    },

    {
      title: "Yearly Average",
      dataIndex: "yearly_average",
      key: "yearly_average",
      align: "center",

      sorter: (a, b) =>
        getAverage(
          a.yearly_average
        ) -
        getAverage(
          b.yearly_average
        ),

      render: (value) => {
        const average =
          getAverage(value);

        const tone =
          getAverageTone(
            average
          );

        return (
          <div
            style={{
              minWidth: 155,
            }}
          >
            <div
              style={{
                display:
                  "flex",
                justifyContent:
                  "center",
                marginBottom: 5,
              }}
            >
              <Tag
                bordered={false}
                style={{
                  background:
                    tone.background,
                  color:
                    tone.color,
                  border:
                    `1px solid ${tone.border}`,
                  borderRadius: 20,
                  fontWeight: 900,
                  padding:
                    "4px 13px",
                }}
              >
                {average.toFixed(
                  1
                )}
                %
              </Tag>
            </div>

            <Progress
              percent={Math.min(
                Math.max(
                  average,
                  0
                ),
                100
              )}
              showInfo={false}
              size="small"
              strokeColor={
                average >= 95
                  ? COLORS.gold
                  : COLORS.brightBlue
              }
              trailColor="#E6ECF2"
            />
          </div>
        );
      },
    },
  ];


  return (
    <div
      className="high-averages-page"
      style={{
        minHeight:
          "100vh",

        background:
          "linear-gradient(180deg,#EEF3F8 0%,#F8FAFC 42%,#FFFFFF 100%)",

        padding:
          "24px clamp(14px,3vw,34px)",

        color:
          COLORS.text,

        overflow:
          "hidden",
      }}
    >

      {/* ======================================================
          HEADER
      ====================================================== */}

      <Card
        bordered={false}
        className="premium-header"
        style={{
          position:
            "relative",

          overflow:
            "hidden",

          borderRadius:
            24,

          marginBottom:
            24,

          background:
            "linear-gradient(125deg,#020A14 0%,#061A33 35%,#0A2A52 72%,#061A33 100%)",

          boxShadow:
            "0 20px 55px rgba(3,16,31,0.28)",

          border:
            "1px solid rgba(212,160,23,0.20)",
        }}
        bodyStyle={{
          padding: 0,
        }}
      >

        <div
          className="header-orb header-orb-one"
        />

        <div
          className="header-orb header-orb-two"
        />

        <div
          className="header-grid"
        />

        <div
          style={{
            position:
              "relative",

            zIndex: 3,

            padding:
              "30px clamp(20px,4vw,40px)",
          }}
        >
          <Row
            justify="space-between"
            align="middle"
            gutter={[
              24,
              24,
            ]}
          >

            <Col
              xs={24}
              md={18}
            >
              <Space
                direction="vertical"
                size={7}
              >

                <div
                  style={{
                    display:
                      "flex",

                    alignItems:
                      "center",

                    gap: 9,
                  }}
                >
                  <div
                    className="pulse-gold"
                    style={{
                      width: 9,
                      height: 9,
                      borderRadius:
                        "50%",
                      background:
                        COLORS.brightGold,
                      boxShadow:
                        "0 0 0 5px rgba(244,197,66,0.10)",
                    }}
                  />

                  <Text
                    style={{
                      color:
                        COLORS.brightGold,

                      fontSize: 11,

                      fontWeight: 900,

                      letterSpacing: 2.2,
                    }}
                  >
                    IGNITE S VOCATIONAL SCHOOL
                  </Text>
                </div>


                <Title
                  level={1}
                  style={{
                    color:
                      COLORS.white,

                    margin: 0,

                    fontSize:
                      "clamp(26px,4vw,40px)",

                    fontWeight: 900,

                    letterSpacing:
                      -0.8,
                  }}
                >
                  Top Performing Students
                </Title>


                <Text
                  style={{
                    color:
                      "rgba(255,255,255,0.73)",

                    fontSize: 14,

                    maxWidth: 700,

                    lineHeight: 1.7,
                  }}
                >
                  Review yearly academic
                  performance and identify
                  students achieving the highest
                  averages within their grade class.
                </Text>


                <Space
                  wrap
                  size={[
                    8,
                    8,
                  ]}
                  style={{
                    marginTop: 7,
                  }}
                >

                  <Tag
                    bordered={false}
                    icon={
                      <BarChartOutlined />
                    }
                    style={{
                      background:
                        "rgba(255,255,255,0.08)",

                      color:
                        COLORS.white,

                      border:
                        "1px solid rgba(255,255,255,0.13)",

                      borderRadius:
                        20,

                      padding:
                        "5px 12px",

                      fontWeight:
                        600,
                    }}
                  >
                    Academic Year:{" "}
                    {selectedYear?.name ||
                      "Loading..."}
                  </Tag>


                  {selectedClassName && (
                    <Tag
                      bordered={false}
                      icon={
                        <TeamOutlined />
                      }
                      style={{
                        background:
                          "rgba(244,197,66,0.10)",

                        color:
                          COLORS.brightGold,

                        border:
                          "1px solid rgba(244,197,66,0.22)",

                        borderRadius:
                          20,

                        padding:
                          "5px 12px",

                        fontWeight:
                          700,
                      }}
                    >
                      {selectedClassName}
                    </Tag>
                  )}

                </Space>
              </Space>
            </Col>


            <Col
              xs={24}
              md={6}
            >
              <div
                style={{
                  display:
                    "flex",

                  justifyContent:
                    "flex-end",
                }}
              >

                <div
                  className="animated-trophy"
                  style={{
                    width: 102,
                    height: 102,

                    borderRadius:
                      25,

                    display:
                      "flex",

                    alignItems:
                      "center",

                    justifyContent:
                      "center",

                    background:
                      "linear-gradient(145deg,#F4C542,#B8860B)",

                    color:
                      COLORS.deepNavy,

                    border:
                      "4px solid rgba(255,255,255,0.10)",

                    boxShadow:
                      "0 15px 40px rgba(0,0,0,0.30)",
                  }}
                >
                  <TrophyOutlined
                    style={{
                      fontSize:
                        47,
                    }}
                  />
                </div>

              </div>
            </Col>

          </Row>
        </div>


        <div
          className="gold-line"
        />
      </Card>


      {/* ======================================================
          SUMMARY
      ====================================================== */}

      <Row
        gutter={[
          16,
          16,
        ]}
        style={{
          marginBottom:
            24,
        }}
      >

        <Col
          xs={24}
          sm={12}
          lg={6}
        >
          <Card
            bordered={false}
            className="metric-card"
            style={{
              height:
                "100%",

              borderRadius:
                17,

              borderTop:
                `3px solid ${COLORS.gold}`,

              background:
                COLORS.white,

              boxShadow:
                "0 9px 28px rgba(6,26,51,0.08)",
            }}
          >
            <Statistic
              title={
                <Text
                  style={{
                    color:
                      COLORS.muted,
                    fontWeight:
                      700,
                  }}
                >
                  Top Performer
                </Text>
              }

              value={
                topStudent?.student_name ||
                "--"
              }

              prefix={
                <CrownOutlined
                  style={{
                    color:
                      COLORS.gold,
                  }}
                />
              }

              valueStyle={{
                color:
                  COLORS.navy,

                fontSize:
                  18,

                fontWeight:
                  900,
              }}
            />

            <Divider
              style={{
                margin:
                  "11px 0",
              }}
            />

            <Tag
              bordered={false}
              style={{
                background:
                  COLORS.softGold,

                color:
                  COLORS.richGold,

                fontWeight:
                  900,

                borderRadius:
                  20,
              }}
            >
              {topStudent
                ? `${getAverage(
                    topStudent.yearly_average
                  ).toFixed(1)}%`
                : "No Data"}
            </Tag>
          </Card>
        </Col>


        <Col
          xs={24}
          sm={12}
          lg={6}
        >
          <Card
            bordered={false}
            className="metric-card"
            style={{
              height:
                "100%",

              borderRadius:
                17,

              borderTop:
                `3px solid ${COLORS.brightBlue}`,

              background:
                COLORS.white,

              boxShadow:
                "0 9px 28px rgba(6,26,51,0.08)",
            }}
          >
            <Statistic
              title={
                <Text
                  style={{
                    color:
                      COLORS.muted,
                    fontWeight:
                      700,
                  }}
                >
                  Qualified Students
                </Text>
              }

              value={
                students.length
              }

              prefix={
                <TeamOutlined
                  style={{
                    color:
                      COLORS.brightBlue,
                  }}
                />
              }

              valueStyle={{
                color:
                  COLORS.navy,

                fontSize:
                  28,

                fontWeight:
                  900,
              }}
            />
          </Card>
        </Col>


        <Col
          xs={24}
          sm={12}
          lg={6}
        >
          <Card
            bordered={false}
            className="metric-card"
            style={{
              height:
                "100%",

              borderRadius:
                17,

              borderTop:
                `3px solid ${COLORS.navy}`,

              background:
                COLORS.white,

              boxShadow:
                "0 9px 28px rgba(6,26,51,0.08)",
            }}
          >
            <Statistic
              title={
                <Text
                  style={{
                    color:
                      COLORS.muted,
                    fontWeight:
                      700,
                  }}
                >
                  Class Average
                </Text>
              }

              value={
                students.length
                  ? classAverage
                  : "--"
              }

              suffix={
                students.length
                  ? "%"
                  : ""
              }

              prefix={
                <RiseOutlined
                  style={{
                    color:
                      COLORS.brightBlue,
                  }}
                />
              }

              valueStyle={{
                color:
                  COLORS.navy,

                fontSize:
                  28,

                fontWeight:
                  900,
              }}
            />
          </Card>
        </Col>


        <Col
          xs={24}
          sm={12}
          lg={6}
        >
          <Card
            bordered={false}
            className="metric-card"
            style={{
              height:
                "100%",

              borderRadius:
                17,

              borderTop:
                `3px solid ${COLORS.gold}`,

              background:
                COLORS.white,

              boxShadow:
                "0 9px 28px rgba(6,26,51,0.08)",
            }}
          >
            <Statistic
              title={
                <Text
                  style={{
                    color:
                      COLORS.muted,
                    fontWeight:
                      700,
                  }}
                >
                  Report Status
                </Text>
              }

              value={
                selectedClass
                  ? "Ready"
                  : "Select Class"
              }

              prefix={
                <SafetyCertificateOutlined
                  style={{
                    color:
                      selectedClass
                        ? COLORS.gold
                        : COLORS.muted,
                  }}
                />
              }

              valueStyle={{
                color:
                  selectedClass
                    ? COLORS.navy
                    : COLORS.muted,

                fontSize:
                  18,

                fontWeight:
                  900,
              }}
            />
          </Card>
        </Col>

      </Row>


      {/* ======================================================
          FILTER PANEL
      ====================================================== */}

      <Card
        bordered={false}
        style={{
          borderRadius:
            19,

          marginBottom:
            24,

          background:
            "linear-gradient(135deg,#FFFFFF,#F7F9FC)",

          boxShadow:
            "0 9px 30px rgba(6,26,51,0.07)",

          border:
            "1px solid #E0E6ED",
        }}
        bodyStyle={{
          padding:
            "21px clamp(16px,3vw,27px)",
        }}
      >

        <Row
          align="middle"
          gutter={[
            18,
            18,
          ]}
        >

          <Col
            xs={24}
            md={10}
            lg={8}
          >
            <Space
              direction="vertical"
              size={6}
              style={{
                width:
                  "100%",
              }}
            >

              <Text
                strong
                style={{
                  color:
                    COLORS.navy,

                  fontSize:
                    14,
                }}
              >
                Grade Class
              </Text>

              <Select
                showSearch
                allowClear
                value={
                  selectedClass ||
                  undefined
                }
                loading={
                  loadingClasses
                }
                placeholder="Select Grade Class"
                onChange={
                  handleGradeClassChange
                }
                onClear={
                  handleClearClass
                }
                optionFilterProp="label"
                options={
                  gradeClasses.map(
                    (item) => ({
                      value:
                        item.id,
                      label:
                        item.name,
                    })
                  )
                }
                style={{
                  width:
                    "100%",
                }}
                size="large"
              />

            </Space>
          </Col>


          <Col
            xs={24}
            md={14}
            lg={10}
          >
            <Space
              direction="vertical"
              size={6}
              style={{
                width:
                  "100%",
              }}
            >

              <Text
                strong
                style={{
                  color:
                    COLORS.navy,

                  fontSize:
                    14,
                }}
              >
                Search Student
              </Text>

              <Input
                allowClear
                size="large"

                prefix={
                  <SearchOutlined
                    style={{
                      color:
                        COLORS.brightBlue,
                    }}
                  />
                }

                placeholder={
                  selectedClass
                    ? "Search student by name..."
                    : "Select a grade class first..."
                }

                value={
                  search
                }

                disabled={
                  !selectedClass
                }

                onChange={(e) =>
                  setSearch(
                    e.target.value
                  )
                }
              />

            </Space>
          </Col>


          <Col
            xs={24}
            lg={6}
          >
            <div
              style={{
                display:
                  "flex",

                justifyContent:
                  "flex-end",

                height:
                  "100%",
              }}
            >

              <Button
                size="large"
                icon={
                  <ReloadOutlined />
                }
                onClick={
                  loadStudents
                }
                loading={
                  loading
                }
                disabled={
                  !selectedClass
                }
                style={{
                  width:
                    "100%",

                  maxWidth:
                    190,

                  borderColor:
                    COLORS.navyBorder,

                  color:
                    COLORS.navy,

                  fontWeight:
                    700,
                }}
              >
                Refresh Results
              </Button>

            </div>
          </Col>

        </Row>
      </Card>


      {/* ======================================================
          PODIUM
      ====================================================== */}

      {students.length > 0 && (
        <Card
          bordered={false}
          style={{
            borderRadius:
              21,

            marginBottom:
              24,

            overflow:
              "hidden",

            background:
              "linear-gradient(135deg,#FFFFFF 0%,#F4F7FA 100%)",

            boxShadow:
              "0 12px 35px rgba(6,26,51,0.09)",

            border:
              "1px solid #DFE5EC",
          }}

          bodyStyle={{
            padding: 0,
          }}
        >

          <div
            style={{
              padding:
                "18px 22px",

              borderBottom:
                "1px solid #E0E6ED",

              background:
                "linear-gradient(90deg,#FFFFFF,#F7F9FC)",
            }}
          >

            <Row
              justify="space-between"
              align="middle"
              gutter={[
                12,
                12,
              ]}
            >

              <Col>

                <Space>

                  <div
                    style={{
                      width: 40,
                      height: 40,

                      borderRadius:
                        11,

                      display:
                        "flex",

                      alignItems:
                        "center",

                      justifyContent:
                        "center",

                      background:
                        COLORS.softGold,

                      color:
                        COLORS.gold,

                      boxShadow:
                        "inset 0 0 0 1px #EAD58B",
                    }}
                  >
                    <GoldOutlined
                      style={{
                        fontSize:
                          19,
                      }}
                    />
                  </div>

                  <div>

                    <Text
                      strong
                      style={{
                        color:
                          COLORS.navy,

                        fontSize:
                          16,
                      }}
                    >
                      Academic Leaders
                    </Text>

                    <div>
                      <Text
                        type="secondary"
                        style={{
                          fontSize:
                            12,
                        }}
                      >
                        Highest yearly averages
                        in the selected class
                      </Text>
                    </div>

                  </div>

                </Space>

              </Col>


              <Col>

                <Tag
                  bordered={false}
                  style={{
                    margin: 0,

                    background:
                      "#EEF3F8",

                    color:
                      COLORS.navy,

                    border:
                      "1px solid #D2DDE8",

                    borderRadius:
                      20,

                    fontWeight:
                      800,
                  }}
                >
                  {selectedClassName ||
                    "Selected Class"}
                </Tag>

              </Col>

            </Row>

          </div>


          <div
            style={{
              padding:
                "24px clamp(14px,4vw,36px) 30px",

              background:
                "linear-gradient(180deg,#F8FAFC,#FFFFFF)",
            }}
          >

            <Row
              gutter={[
                18,
                22,
              ]}
              align="bottom"
            >

              {/* SECOND */}

              <Col
                xs={24}
                md={8}
              >
                {secondStudent ? (
                  <div
                    className="podium-side-card"
                    style={{
                      minHeight:
                        225,

                      padding:
                        22,

                      textAlign:
                        "center",

                      borderRadius:
                        19,

                      background:
                        COLORS.white,

                      border:
                        "1px solid #D9E1EA",

                      boxShadow:
                        "0 8px 23px rgba(6,26,51,0.07)",
                    }}
                  >

                    <Avatar
                      size={66}
                      style={{
                        background:
                          "linear-gradient(145deg,#F1F4F7,#C6CED8)",

                        color:
                          COLORS.navy,

                        border:
                          "3px solid white",

                        boxShadow:
                          "0 6px 18px rgba(6,26,51,0.14)",

                        fontSize:
                          21,

                        fontWeight:
                          900,
                      }}
                    >
                      {getInitials(
                        secondStudent.student_name
                      )}
                    </Avatar>

                    <div
                      style={{
                        marginTop:
                          12,
                      }}
                    >
                      <Tag
                        bordered={false}
                        style={{
                          background:
                            "#EEF1F4",

                          color:
                            COLORS.navy,

                          borderRadius:
                            20,

                          fontWeight:
                            900,
                        }}
                      >
                        2ND PLACE
                      </Tag>
                    </div>

                    <Title
                      level={5}
                      style={{
                        margin:
                          "10px 0 6px",

                        color:
                          COLORS.text,
                      }}
                    >
                      {
                        secondStudent.student_name
                      }
                    </Title>

                    <Text
                      strong
                      style={{
                        color:
                          COLORS.navy,

                        fontSize:
                          20,
                      }}
                    >
                      {getAverage(
                        secondStudent.yearly_average
                      ).toFixed(1)}
                      %
                    </Text>

                  </div>
                ) : (
                  <div
                    style={{
                      minHeight:
                        225,
                    }}
                  />
                )}
              </Col>


              {/* FIRST */}

              <Col
                xs={24}
                md={8}
              >
                {topStudent && (
                  <div
                    className="top-performer-card"
                    style={{
                      position:
                        "relative",

                      minHeight:
                        285,

                      padding:
                        "28px 22px",

                      textAlign:
                        "center",

                      borderRadius:
                        22,

                      background:
                        "linear-gradient(145deg,#020A14 0%,#061A33 42%,#0A2A52 100%)",

                      border:
                        `2px solid ${COLORS.gold}`,

                      boxShadow:
                        "0 18px 40px rgba(3,16,31,0.25)",

                      overflow:
                        "hidden",
                    }}
                  >

                    <div
                      className="podium-glow"
                    />

                    <div
                      style={{
                        position:
                          "relative",

                        zIndex: 2,
                      }}
                    >

                      <div
                        style={{
                          display:
                            "inline-flex",

                          alignItems:
                            "center",

                          gap: 7,

                          padding:
                            "6px 13px",

                          borderRadius:
                            20,

                          background:
                            "rgba(244,197,66,0.10)",

                          border:
                            "1px solid rgba(244,197,66,0.28)",

                          color:
                            COLORS.brightGold,

                          fontSize:
                            10,

                          fontWeight:
                            900,

                          letterSpacing:
                            1.1,
                        }}
                      >
                        <CrownOutlined />
                        CLASS TOP PERFORMER
                      </div>


                      <div
                        className="winner-avatar"
                        style={{
                          margin:
                            "18px 0 13px",
                        }}
                      >
                        <Avatar
                          size={91}
                          style={{
                            background:
                              "linear-gradient(145deg,#F4C542,#B8860B)",

                            color:
                              COLORS.deepNavy,

                            border:
                              "4px solid rgba(255,255,255,0.14)",

                            boxShadow:
                              "0 10px 28px rgba(0,0,0,0.30)",

                            fontSize:
                              28,

                            fontWeight:
                              900,
                          }}
                        >
                          {getInitials(
                            topStudent.student_name
                          )}
                        </Avatar>
                      </div>


                      <Title
                        level={3}
                        style={{
                          color:
                            COLORS.white,

                          margin:
                            "0 0 8px",

                          fontSize:
                            21,

                          fontWeight:
                            800,
                        }}
                      >
                        {
                          topStudent.student_name
                        }
                      </Title>


                      <div
                        style={{
                          fontSize:
                            33,

                          fontWeight:
                            900,

                          color:
                            COLORS.brightGold,

                          lineHeight:
                            1.05,
                        }}
                      >
                        {getAverage(
                          topStudent.yearly_average
                        ).toFixed(1)}
                        %
                      </div>


                      <Text
                        style={{
                          color:
                            "rgba(255,255,255,0.66)",

                          fontSize:
                            12,
                        }}
                      >
                        Yearly Average
                      </Text>

                    </div>

                  </div>
                )}
              </Col>


              {/* THIRD */}

              <Col
                xs={24}
                md={8}
              >
                {thirdStudent ? (
                  <div
                    className="podium-side-card"
                    style={{
                      minHeight:
                        225,

                      padding:
                        22,

                      textAlign:
                        "center",

                      borderRadius:
                        19,

                      background:
                        COLORS.white,

                      border:
                        "1px solid #D9E1EA",

                      boxShadow:
                        "0 8px 23px rgba(6,26,51,0.07)",
                    }}
                  >

                    <Avatar
                      size={66}
                      style={{
                        background:
                          "linear-gradient(145deg,#D9B36C,#A97A25)",

                        color:
                          COLORS.white,

                        border:
                          "3px solid white",

                        boxShadow:
                          "0 6px 18px rgba(168,120,37,0.16)",

                        fontSize:
                          21,

                        fontWeight:
                          900,
                      }}
                    >
                      {getInitials(
                        thirdStudent.student_name
                      )}
                    </Avatar>

                    <div
                      style={{
                        marginTop:
                          12,
                      }}
                    >
                      <Tag
                        bordered={false}
                        style={{
                          background:
                            "#FFF7E5",

                          color:
                            COLORS.richGold,

                          borderRadius:
                            20,

                          fontWeight:
                            900,
                        }}
                      >
                        3RD PLACE
                      </Tag>
                    </div>

                    <Title
                      level={5}
                      style={{
                        margin:
                          "10px 0 6px",

                        color:
                          COLORS.text,
                      }}
                    >
                      {
                        thirdStudent.student_name
                      }
                    </Title>

                    <Text
                      strong
                      style={{
                        color:
                          COLORS.richGold,

                        fontSize:
                          20,
                      }}
                    >
                      {getAverage(
                        thirdStudent.yearly_average
                      ).toFixed(1)}
                      %
                    </Text>

                  </div>
                ) : (
                  <div
                    style={{
                      minHeight:
                        225,
                    }}
                  />
                )}
              </Col>

            </Row>

          </div>
        </Card>
      )}


      {/* ======================================================
          TABLE
      ====================================================== */}

      <Card
        bordered={false}
        style={{
          borderRadius:
            21,

          overflow:
            "hidden",

          background:
            COLORS.white,

          boxShadow:
            "0 12px 35px rgba(6,26,51,0.09)",

          border:
            "1px solid #DFE5EC",
        }}

        bodyStyle={{
          padding: 0,
        }}
      >

        <div
          style={{
            padding:
              "18px 22px",

            borderBottom:
              "1px solid #E0E6ED",

            background:
              "linear-gradient(90deg,#FFFFFF,#F7F9FC)",
          }}
        >

          <Row
            justify="space-between"
            align="middle"
            gutter={[
              15,
              15,
            ]}
          >

            <Col>

              <Space>

                <div
                  style={{
                    width: 41,
                    height: 41,

                    borderRadius:
                      11,

                    display:
                      "flex",

                    alignItems:
                      "center",

                    justifyContent:
                      "center",

                    background:
                      COLORS.navy,

                    color:
                      COLORS.brightGold,

                    boxShadow:
                      "0 6px 15px rgba(6,26,51,0.16)",
                  }}
                >
                  <TeamOutlined
                    style={{
                      fontSize:
                        20,
                    }}
                  />
                </div>


                <div>

                  <Text
                    strong
                    style={{
                      display:
                        "block",

                      color:
                        COLORS.navy,

                      fontSize:
                        16,
                    }}
                  >
                    Student Performance Ranking
                  </Text>

                  <Text
                    type="secondary"
                    style={{
                      fontSize:
                        12,
                    }}
                  >
                    {selectedClass
                      ? `${students.length} qualified student${
                          students.length === 1
                            ? ""
                            : "s"
                        }`
                      : "Select a grade class to view results"}
                  </Text>

                </div>

              </Space>

            </Col>


            <Col>

              <Tooltip
                title={
                  students.length
                    ? "Generate the official PDF report"
                    : "Select a class with available results"
                }
              >
                <Button
                  type="primary"
                  icon={
                    <DownloadOutlined />
                  }

                  onClick={
                    handleDownloadReport
                  }

                  disabled={
                    !students.length
                  }

                  style={{
                    height:
                      43,

                    border:
                      0,

                    borderRadius:
                      10,

                    background:
                      students.length
                        ? "linear-gradient(135deg,#061A33,#0D3B73)"
                        : undefined,

                    boxShadow:
                      students.length
                        ? "0 7px 17px rgba(6,26,51,0.22)"
                        : undefined,

                    fontWeight:
                      800,
                  }}
                >
                  Download PDF
                </Button>
              </Tooltip>

            </Col>

          </Row>

        </div>


        <div
          style={{
            padding:
              "0 14px 16px",
          }}
        >

          {loading ? (

            <div
              style={{
                minHeight:
                  320,

                display:
                  "flex",

                alignItems:
                  "center",

                justifyContent:
                  "center",
              }}
            >

              <Space
                direction="vertical"
                align="center"
              >

                <Spin
                  size="large"
                />

                <Text
                  type="secondary"
                >
                  Loading performance results...
                </Text>

              </Space>

            </div>

          ) : error ? (

            <div
              style={{
                padding:
                  "28px 10px",
              }}
            >
              <Alert
                showIcon
                message="Unable to load results"
                description={
                  error
                }

                type="error"

                action={
                  <Button
                    size="small"
                    onClick={
                      loadStudents
                    }
                  >
                    Try Again
                  </Button>
                }
              />
            </div>

          ) : !selectedClass ? (

            <div
              style={{
                padding:
                  "60px 20px",
              }}
            >

              <Empty
                image={
                  <div
                    style={{
                      width:
                        78,

                      height:
                        78,

                      borderRadius:
                        21,

                      margin:
                        "0 auto",

                      display:
                        "flex",

                      alignItems:
                        "center",

                      justifyContent:
                        "center",

                      background:
                        COLORS.navy,

                      color:
                        COLORS.brightGold,

                      boxShadow:
                        "0 10px 25px rgba(6,26,51,0.17)",
                    }}
                  >
                    <TrophyOutlined
                      style={{
                        fontSize:
                          35,
                      }}
                    />
                  </div>
                }

                description={
                  <Space
                    direction="vertical"
                    size={2}
                  >

                    <Text
                      strong
                      style={{
                        color:
                          COLORS.navy,
                      }}
                    >
                      Select a Grade Class
                    </Text>

                    <Text
                      type="secondary"
                    >
                      Choose a class above to view
                      high-performing students.
                    </Text>

                  </Space>
                }
              />

            </div>

          ) : !students.length ? (

            <div
              style={{
                padding:
                  "60px 20px",
              }}
            >

              <Empty
                description={
                  <Space
                    direction="vertical"
                    size={2}
                  >

                    <Text
                      strong
                      style={{
                        color:
                          COLORS.navy,
                      }}
                    >
                      No qualifying students found
                    </Text>

                    <Text
                      type="secondary"
                    >
                      There are no matching performance
                      records for this class and search criteria.
                    </Text>

                  </Space>
                }
              />

            </div>

          ) : (

            <Table
              columns={
                columns
              }

              dataSource={
                students
              }

              rowKey="student_id"

              scroll={{
                x: 720,
              }}

              pagination={{
                pageSize:
                  25,

                showSizeChanger:
                  true,

                pageSizeOptions:
                  [
                    "10",
                    "25",
                    "50",
                    "100",
                  ],

                showTotal:
                  (
                    total,
                    range
                  ) =>
                    `${range[0]}-${range[1]} of ${total} students`,
              }}

              rowClassName={() =>
                "navy-performance-row"
              }
            />

          )}

        </div>

      </Card>


      {/* ======================================================
          PDF MODAL
      ====================================================== */}

      <Modal
        open={
          showPDFModal
        }

        onCancel={() =>
          setShowPDFModal(
            false
          )
        }

        footer={null}

        centered

        width={520}

        destroyOnClose

        styles={{
          content: {
            borderRadius:
              20,

            overflow:
              "hidden",

            padding:
              0,
          },

          body: {
            padding:
              0,
          },
        }}
      >

        <div
          style={{
            background:
              "linear-gradient(135deg,#020A14,#061A33 55%,#0D3B73)",

            padding:
              "25px 27px",

            position:
              "relative",

            overflow:
              "hidden",
          }}
        >

          <div
            className="modal-orb"
          />

          <Space
            align="start"
            size={14}
          >

            <div
              style={{
                width:
                  49,

                height:
                  49,

                borderRadius:
                  14,

                display:
                  "flex",

                alignItems:
                  "center",

                justifyContent:
                  "center",

                background:
                  "linear-gradient(145deg,#F4C542,#B8860B)",

                color:
                  COLORS.deepNavy,

                boxShadow:
                  "0 8px 20px rgba(0,0,0,0.22)",
              }}
            >
              <FilePdfOutlined
                style={{
                  fontSize:
                    23,
                }}
              />
            </div>


            <div>

              <Title
                level={4}
                style={{
                  color:
                    COLORS.white,

                  margin:
                    0,

                  fontWeight:
                    800,
                }}
              >
                High Average Report
              </Title>

              <Text
                style={{
                  color:
                    "rgba(255,255,255,0.68)",

                  fontSize:
                    12,
                }}
              >
                IGNITE S VOCATIONAL SCHOOL
              </Text>

            </div>

          </Space>

        </div>


        <div
          style={{
            padding:
              27,
          }}
        >

          <Text
            style={{
              color:
                COLORS.text,

              fontSize:
                14,

              lineHeight:
                1.7,
            }}
          >
            Generate the official high-average
            performance report for the selected
            grade class.
          </Text>


          <div
            style={{
              marginTop:
                18,

              padding:
                17,

              borderRadius:
                14,

              background:
                "#F6F8FA",

              border:
                "1px solid #DFE5EC",
            }}
          >

            <Row
              gutter={[
                12,
                12,
              ]}
            >

              <Col
                span={12}
              >
                <Text
                  type="secondary"
                  style={{
                    fontSize:
                      10,

                    fontWeight:
                      800,
                  }}
                >
                  ACADEMIC YEAR
                </Text>

                <div>

                  <Text
                    strong
                    style={{
                      color:
                        COLORS.navy,
                    }}
                  >
                    {selectedYear?.name ||
                      reportData?.academic_year ||
                      "--"}
                  </Text>

                </div>
              </Col>


              <Col
                span={12}
              >
                <Text
                  type="secondary"
                  style={{
                    fontSize:
                      10,

                    fontWeight:
                      800,
                  }}
                >
                  GRADE CLASS
                </Text>

                <div>

                  <Text
                    strong
                    style={{
                      color:
                        COLORS.navy,
                    }}
                  >
                    {reportData?.grade_class ||
                      selectedClassName ||
                      "--"}
                  </Text>

                </div>
              </Col>


              <Col
                span={24}
              >
                <Divider
                  style={{
                    margin:
                      "4px 0",
                  }}
                />
              </Col>


              <Col
                span={12}
              >
                <Text
                  type="secondary"
                  style={{
                    fontSize:
                      10,

                    fontWeight:
                      800,
                  }}
                >
                  QUALIFIED STUDENTS
                </Text>

                <div>

                  <Text
                    strong
                    style={{
                      color:
                        COLORS.brightBlue,

                      fontSize:
                        18,
                    }}
                  >
                    {students.length}
                  </Text>

                </div>
              </Col>


              <Col
                span={12}
              >
                <Text
                  type="secondary"
                  style={{
                    fontSize:
                      10,

                    fontWeight:
                      800,
                  }}
                >
                  TOP AVERAGE
                </Text>

                <div>

                  <Text
                    strong
                    style={{
                      color:
                        COLORS.richGold,

                      fontSize:
                        18,
                    }}
                  >
                    {topStudent
                      ? `${getAverage(
                          topStudent.yearly_average
                        ).toFixed(1)}%`
                      : "--"}
                  </Text>

                </div>
              </Col>

            </Row>

          </div>


          <Space
            style={{
              width:
                "100%",

              marginTop:
                23,

              justifyContent:
                "flex-end",
            }}

            wrap
          >

            <Button
              onClick={() =>
                setShowPDFModal(
                  false
                )
              }
            >
              Cancel
            </Button>


            <PDFDownloadLink
              document={
                <HighAveragesPagePDF
                  data={
                    reportData
                  }
                />
              }

              fileName={`High_Averages_${
                reportData?.grade_class ||
                selectedClassName ||
                "Report"
              }.pdf`}
            >
              {({
                loading,
              }) => (
                <Button
                  type="primary"
                  icon={
                    <DownloadOutlined />
                  }
                  loading={
                    loading
                  }

                  style={{
                    border:
                      0,

                    background:
                      "linear-gradient(135deg,#061A33,#0D3B73)",

                    fontWeight:
                      800,

                    boxShadow:
                      "0 7px 16px rgba(6,26,51,0.20)",
                  }}
                >
                  {loading
                    ? "Preparing Report..."
                    : "Download PDF"}
                </Button>
              )}
            </PDFDownloadLink>

          </Space>

        </div>

      </Modal>


      {/* ======================================================
          ANIMATIONS + TABLE STYLING
      ====================================================== */}

      <style>
        {`

          .high-averages-page {
            position: relative;
          }

          .premium-header {
            animation: headerEntrance 0.65s ease-out;
          }

          @keyframes headerEntrance {
            from {
              opacity: 0;
              transform: translateY(-12px);
            }

            to {
              opacity: 1;
              transform: translateY(0);
            }
          }

          .header-orb {
            position: absolute;
            border-radius: 50%;
            pointer-events: none;
          }

          .header-orb-one {
            width: 280px;
            height: 280px;
            right: -110px;
            top: -150px;
            border: 1px solid rgba(244,197,66,0.18);
            background: rgba(244,197,66,0.035);
            animation: orbFloatOne 7s ease-in-out infinite;
          }

          .header-orb-two {
            width: 210px;
            height: 210px;
            right: 140px;
            bottom: -160px;
            border: 1px solid rgba(47,128,201,0.18);
            background: rgba(47,128,201,0.04);
            animation: orbFloatTwo 8s ease-in-out infinite;
          }

          @keyframes orbFloatOne {
            0%, 100% {
              transform: translate(0, 0) rotate(0deg);
            }

            50% {
              transform: translate(-18px, 14px) rotate(7deg);
            }
          }

          @keyframes orbFloatTwo {
            0%, 100% {
              transform: translate(0, 0);
            }

            50% {
              transform: translate(14px, -12px);
            }
          }

          .header-grid {
            position: absolute;
            inset: 0;
            opacity: 0.08;
            background-image:
              linear-gradient(rgba(255,255,255,0.15) 1px, transparent 1px),
              linear-gradient(90deg, rgba(255,255,255,0.15) 1px, transparent 1px);
            background-size: 34px 34px;
            mask-image: linear-gradient(to right, transparent, black, transparent);
            pointer-events: none;
          }

          .gold-line {
            height: 4px;
            background:
              linear-gradient(
                90deg,
                #B8860B 0%,
                #F4C542 30%,
                #FFFFFF 50%,
                #F4C542 70%,
                #B8860B 100%
              );
            background-size: 220% 100%;
            animation: goldSweep 5s linear infinite;
          }

          @keyframes goldSweep {
            0% {
              background-position: 0% 50%;
            }

            100% {
              background-position: 220% 50%;
            }
          }

          .pulse-gold {
            animation: goldPulse 2s ease-in-out infinite;
          }

          @keyframes goldPulse {
            0%, 100% {
              box-shadow:
                0 0 0 4px rgba(244,197,66,0.10);
            }

            50% {
              box-shadow:
                0 0 0 9px rgba(244,197,66,0.02);
            }
          }

          .animated-trophy {
            animation:
              trophyFloat 4s ease-in-out infinite,
              trophyGlow 2.8s ease-in-out infinite alternate;
          }

          @keyframes trophyFloat {
            0%, 100% {
              transform: translateY(0) rotate(2deg);
            }

            50% {
              transform: translateY(-9px) rotate(-2deg);
            }
          }

          @keyframes trophyGlow {
            from {
              box-shadow:
                0 15px 35px rgba(0,0,0,0.28);
            }

            to {
              box-shadow:
                0 18px 45px rgba(244,197,66,0.25);
            }
          }

          .metric-card {
            transition:
              transform 0.25s ease,
              box-shadow 0.25s ease;
          }

          .metric-card:hover {
            transform:
              translateY(-5px);
            box-shadow:
              0 15px 35px rgba(6,26,51,0.13) !important;
          }

          .podium-side-card {
            transition:
              transform 0.28s ease,
              box-shadow 0.28s ease,
              border-color 0.28s ease;
          }

          .podium-side-card:hover {
            transform:
              translateY(-7px);
            border-color:
              #C8D3DF !important;
            box-shadow:
              0 15px 30px rgba(6,26,51,0.11) !important;
          }

          .top-performer-card {
            animation:
              winnerEntrance 0.7s ease-out,
              winnerFloat 5s ease-in-out 0.8s infinite;
          }

          @keyframes winnerEntrance {
            from {
              opacity: 0;
              transform:
                translateY(18px)
                scale(0.97);
            }

            to {
              opacity: 1;
              transform:
                translateY(0)
                scale(1);
            }
          }

          @keyframes winnerFloat {
            0%, 100% {
              transform:
                translateY(0);
            }

            50% {
              transform:
                translateY(-5px);
            }
          }

          .winner-avatar {
            animation:
              winnerAvatar 3.2s ease-in-out infinite;
          }

          @keyframes winnerAvatar {
            0%, 100% {
              transform:
                scale(1);
            }

            50% {
              transform:
                scale(1.045);
            }
          }

          .podium-glow {
            position: absolute;
            width: 190px;
            height: 190px;
            border-radius: 50%;
            top: -105px;
            right: -75px;
            background:
              radial-gradient(
                circle,
                rgba(244,197,66,0.16) 0%,
                rgba(244,197,66,0.04) 45%,
                transparent 70%
              );
            animation:
              podiumGlowMove 5s ease-in-out infinite;
          }

          @keyframes podiumGlowMove {
            0%, 100% {
              transform:
                translate(0,0);
            }

            50% {
              transform:
                translate(-22px,20px);
            }
          }

          .navy-performance-row > td {
            transition:
              background 0.18s ease,
              box-shadow 0.18s ease;
          }

          .navy-performance-row:hover > td {
            background:
              #F5F8FB !important;
          }

          .ant-table-thead > tr > th {
            background:
              #F1F5F9 !important;

            color:
              #061A33 !important;

            font-weight:
              800 !important;

            border-bottom:
              1px solid #D7E0EA !important;
          }

          .ant-table-tbody > tr > td {
            border-bottom:
              1px solid #E6EBF0 !important;
          }

          .ant-progress-bg {
            transition:
              width 0.5s ease;
          }

          .modal-orb {
            position: absolute;
            width: 150px;
            height: 150px;
            border-radius: 50%;
            right: -65px;
            top: -75px;
            border:
              1px solid rgba(244,197,66,0.22);
            background:
              rgba(244,197,66,0.035);
            animation:
              modalOrb 5s ease-in-out infinite;
          }

          @keyframes modalOrb {
            0%, 100% {
              transform:
                translate(0,0);
            }

            50% {
              transform:
                translate(-12px,10px);
            }
          }

          .ant-select-selector,
          .ant-input {
            transition:
              border-color 0.2s ease,
              box-shadow 0.2s ease;
          }

          .ant-select-focused
          .ant-select-selector,
          .ant-input:focus {
            border-color:
              #0D3B73 !important;

            box-shadow:
              0 0 0 3px rgba(13,59,115,0.10) !important;
          }

          @media (max-width: 768px) {

            .high-averages-page {
              padding:
                14px !important;
            }

            .ant-table {
              font-size:
                12px;
            }

            .animated-trophy {
              width:
                78px !important;

              height:
                78px !important;
            }

            .animated-trophy .anticon {
              font-size:
                35px !important;
            }
          }

          @media (prefers-reduced-motion: reduce) {

            *,
            *::before,
            *::after {
              animation-duration:
                0.01ms !important;

              animation-iteration-count:
                1 !important;

              transition-duration:
                0.01ms !important;
            }
          }
        `}
      </style>

    </div>
  );
};


export default HighAveragesPage;